// MfaSetupForm.jsx
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import apiClient from "api/apiClient";
import useMessageToast from "hooks/useMessageToast";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { setAuth } from "state/slices/authSlice"; // { isAuthenticated, user }

const API = process.env.REACT_APP_BASE_URL; // https://api.greenleaves.app

const MfaSetupForm = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [mfaEnabled, setMfaEnabled] = useState(!!user?.mfaEnabled);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("");
  useMessageToast(status, "info");
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  // How many single-use backup codes are still live on the account. Each one works ONCE and
  // is deleted when used, and "Generate New Backup Codes" replaces the whole set — so a user
  // can burn through them without ever being told, then be locked out when the authenticator
  // app is gone. That is exactly what happened on 2026-09-18. Surface the number.
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);

  const codesRemaining = Number(user?.mfaBackupCodesRemaining ?? 0);
  const codesSeverity =
    codesRemaining === 0 ? "error" : codesRemaining <= 2 ? "warning" : "info";
  const codesMessage =
    codesRemaining === 0
      ? "No backup codes left. If you lose access to your authenticator app you will be locked out and will need an administrator to reset MFA. Generate a new set now."
      : codesRemaining <= 2
        ? `Only ${codesRemaining} backup ${codesRemaining === 1 ? "code" : "codes"} left. Generate a new set before you run out.`
        : `${codesRemaining} backup codes remaining. Each one works only once.`;

  const syncUserState = (updatedUser) => {
    dispatch(
      setAuth({
        isAuthenticated: true,
        user: { ...(user || {}), ...updatedUser },
      }),
    );
  };

  const copyBackupCodes = async () => {
    if (!backupCodes.length || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setStatus("Backup codes copied. Store them somewhere safe.");
    } catch {
      setStatus("Could not copy backup codes automatically. Please copy them manually.");
    }
  };

  const handleEnableMfa = async () => {
    setLoading(true);
    setStatus("");
    try {
      // cookie-based, CSRF handled by interceptor
      const { data } = await apiClient.get(`${API}/user/setup-mfa`, {
        withCredentials: true,
      });
      setQrCode(data.qrCodeImageUrl);
      setSecret(data.secret);
      setBackupCodes([]);
    } catch (e) {
      setStatus("Failed to load MFA setup.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    setLoading(true);
    setStatus("");
    try {
      // Verify and enable (server marks user.mfaEnabled = true)
      const { data } = await apiClient.post(
        `${API}/user/verify-mfa`,
        { secret, token },
        { withCredentials: true },
      );

      // If server returns the updated user, prefer it; otherwise fetch /user/me
      let updatedUser = data?.user;
      if (!updatedUser) {
        const me = await apiClient.get(`${API}/user/me`, {
          withCredentials: true,
        });
        updatedUser = me.data?.user || me.data;
      }

      // Flip local UI
      setMfaEnabled(true);
      setQrCode("");
      setToken("");
      setBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setStatus("MFA enabled successfully. Save your backup codes now.");
      syncUserState({ ...(updatedUser || {}), mfaEnabled: true });
    } catch (e) {
      setStatus("❌ Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setLoading(true);
    setStatus("");
    try {
      const { data } = await apiClient.post(
        `${API}/user/mfa-backup-codes/regenerate`,
        {},
        { withCredentials: true },
      );

      setBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setStatus("New backup codes generated. Your previous backup codes no longer work.");

      if (data?.user) {
        syncUserState(data.user);
      }
    } catch (e) {
      setStatus("Failed to regenerate backup codes.");
    } finally {
      setLoading(false);
    }
  };

  const closeDisableDialog = () => {
    setDisableOpen(false);
    setDisablePassword("");
    setDisableCode("");
    setDisableError("");
  };

  const handleDisableMfa = async () => {
    setDisableLoading(true);
    setDisableError("");
    try {
      const { data } = await apiClient.post(
        `${API}/user/disable-mfa`,
        { currentPassword: disablePassword, token: disableCode },
        { withCredentials: true },
      );
      setMfaEnabled(false);
      setBackupCodes([]);
      setQrCode("");
      setSecret("");
      setToken("");
      syncUserState(data?.user || { mfaEnabled: false, mfaBackupCodesRemaining: 0 });
      setStatus("MFA is now off. You can turn it back on at any time.");
      closeDisableDialog();
    } catch (e) {
      // Shown inside the dialog, not as a toast: the user has fields to correct and the
      // dialog must stay open so they can.
      setDisableError(
        e.response?.data?.message || "Could not turn off MFA. Please try again.",
      );
    } finally {
      setDisableLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h3>Multi-Factor Authentication</h3>
      <p>Secure your account with TOTP-based two-factor authentication.</p>

      {mfaEnabled ? (
        <Stack spacing={2}>
          <div className="mfa-status enabled">✅ MFA is currently enabled.</div>
          <Alert severity={codesSeverity} sx={{ alignItems: "center" }}>
            {codesMessage}
          </Alert>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              onClick={handleRegenerateBackupCodes}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Generate New Backup Codes"}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setDisableOpen(true)}
              disabled={loading}
            >
              Turn Off MFA
            </Button>
          </Stack>
        </Stack>
      ) : qrCode ? (
        <div className="mfa-setup">
          <img src={qrCode} alt="MFA QR Code" />
          <TextField
            label="Enter 6-digit code"
            variant="outlined"
            fullWidth
            value={token}
            onChange={(e) => setToken(e.target.value)}
            sx={{ margin: "12px 0" }}
            inputProps={{ maxLength: 6 }}
          />
          <Button
            variant="contained"
            onClick={handleVerifyMfa}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Verify & Enable"}
          </Button>
        </div>
      ) : (
        <Button
          variant="contained"
          onClick={handleEnableMfa}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Enable MFA"}
        </Button>
      )}

      {backupCodes.length ? (
        <Alert severity="warning" sx={{ mt: 2, alignItems: "flex-start" }}>
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Save these backup codes now.
            </Typography>
            <Typography variant="body2">
              Each code can be used once if you lose access to your authenticator app.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1,
              }}
            >
              {backupCodes.map((code) => (
                <Box
                  key={code}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    backgroundColor: "rgba(12, 17, 44, 0.65)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    fontFamily: "monospace",
                    fontSize: 14,
                    letterSpacing: "0.08em",
                  }}
                >
                  {code}
                </Box>
              ))}
            </Box>
            <Box>
              {/* color="inherit" so the button picks up the warning Alert's own (light) text
                  colour. The default outlined button uses the theme's primary blue, which on
                  this dark alert was barely legible. */}
              <Button
                variant="outlined"
                color="inherit"
                onClick={copyBackupCodes}
                disabled={loading}
                sx={{
                  borderColor: "currentColor",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "currentColor",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                Copy Backup Codes
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : null}

      {status && <div className="mfa-status-msg">{status}</div>}

      {/* Turning MFA off asks for BOTH factors again — a stolen session must not be enough
          to strip the account's second factor. The server enforces this; the dialog just
          collects it. */}
      <Dialog open={disableOpen} onClose={closeDisableDialog} fullWidth maxWidth="xs">
        <DialogTitle>Turn off multi-factor authentication?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your account will be protected by your password alone. Confirm with your password
            and a current authenticator or backup code.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type="password"
              fullWidth
              autoComplete="current-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              disabled={disableLoading}
            />
            <TextField
              label="Authenticator or backup code"
              fullWidth
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              disabled={disableLoading}
            />
            {disableError ? <Alert severity="error">{disableError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDisableDialog} disabled={disableLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDisableMfa}
            disabled={disableLoading || !disablePassword.trim() || !disableCode.trim()}
          >
            {disableLoading ? <CircularProgress size={20} color="inherit" /> : "Turn off MFA"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MfaSetupForm;
